import React, { useEffect, useState } from "react";
import { Box, Button, Chip, ClickAwayListener, Grid, Grow, Paper, Popper, Stack, Typography } from "@mui/material";
import { useLazyGetProjectSCurveQuery } from "store/slices/apis/scheduleSlice";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import RiskKpi from "./RiskKpi";
import RiskCountBySeverity from "./RiskCountBySeverity";
import RiskByCategory from "./RiskByCategory";
import HeatMap from "./HeatMap";
import ActiveRiskByOwners from "./ActiveRiskByOwners";
import RiskTrend from "./RiskTrend";
import { useLazyGetRiskAgeingQuery, useLazyGetRiskCategorySummaryQuery, useLazyGetRiskHeatMapQuery, useLazyGetRiskKpisQuery, useLazyGetRiskOwnerSummaryQuery, useLazyGetRiskSeveritySummaryQuery, useLazyGetRiskTrendQuery } from "store/slices/apis/riskDashboardSlice";
import RiskAgeingAnalysis from "./RiskAgeingAnalysis";
import ProjectLastUpdatedOn from "components/ProjectLastUpdatedOn";
import { useGetLastUpdatedOnStatusQuery } from "store/slices/apis/projectStatusApiSlice";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { enterpriseTokens } from "styles/theme";

const validationSchema = Yup.object({
    category: Yup.string().nullable(),
    subCategory: Yup.string().nullable(),
    wbsId: Yup.string().nullable(),
    wbsName: Yup.string().nullable(),
});


const RiskDashboard = () => {
    const { projectId } = useParams();

    const { data: riskData } = useGetLastUpdatedOnStatusQuery({ ProjectId: projectId });

    const [isApplyFilter, setIsApplyFIlter] = React.useState(false);

    const [open, setOpen] = useState(false);
    const anchorRef = React.useRef(null);
    const arrowRef = React.useRef(null);

    const [filters, setFilters] = useState({
        projectId: projectId,
    });


    const [riskKpiTrigger, { data: riskKpiData, isLoading, isError, error }] = useLazyGetRiskKpisQuery();
    const [riskSeveritySummaryTrigger, { data: riskSeveritySummaryData }] = useLazyGetRiskSeveritySummaryQuery();
    const [riskCategorySummaryTrigger, { data: riskCategorySummaryData }] = useLazyGetRiskCategorySummaryQuery();
    const [riskOwnerSummaryTrigger, { data: riskOwnerSummaryData }] = useLazyGetRiskOwnerSummaryQuery();
    const [riskTrendTrigger, { data: riskTrendData }] = useLazyGetRiskTrendQuery();
    const [riskHeatMapTrigger, { data: riskHeatMapData }] = useLazyGetRiskHeatMapQuery();
    const [riskAgeingTrigger, { data: riskAgeingData }] = useLazyGetRiskAgeingQuery();

    useEffect(() => {
        riskKpiTrigger(projectId);
        riskSeveritySummaryTrigger(projectId);
        riskCategorySummaryTrigger(projectId);
        riskOwnerSummaryTrigger(projectId);
        riskTrendTrigger(projectId);
        riskHeatMapTrigger(projectId);
        riskAgeingTrigger(projectId);
    }, []);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleApplyFilters = async (values, { setSubmitting }) => {
        console.log("Apply filters →", values);

        const payload = {
            projectId: projectId,
            category: values.category || "",
            subCategory: values.wbsId || "",
        };

        // keep name for showing in Chip / text
        const newFilters = {
            ...payload,
            wbsName: values.wbsName || ""
        };

        console.log("New filters →", newFilters);
        setFilters(newFilters);

        // call your APIs
        // trigger(payload);
        // sCurvetrigger(payload);

        setIsApplyFIlter(true);
        setOpen(false);
        setSubmitting(false);
    };

    const handleReset = (resetForm) => {
        resetForm();
        resetAllFilters();
    };

    // Clears filters + reloads APIs
    const resetAllFilters = () => {
        const resetValues = {
            projectId,
            category: "",
            subCategory: "",
            activity: "",
            wbsId: "",
            wbsName: ""
        };

        setFilters(resetValues);
        setIsApplyFIlter(false);
        setOpen(false);
    };


    const handleDelete = (fieldName) => {
        setFilters(prev => ({
            ...prev,
            [fieldName]: ""
        }));

        const updatedFilters = {
            ...filters,
            [fieldName]: "",
        };

    }

    if (isLoading) {
        return (
            <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                <Typography color="error">
                    Loading KPIs...
                </Typography>
            </Box>
        );
    }
    // if (isError) {
    //     return (
    //         <Box sx={{ p: 2 }}>
    //             <Typography color="error">
    //                 Failed to load Schedule KPIs.
    //             </Typography>

    //         </Box>
    //     );
    // }
    return (
        <>
            <Grid container spacing={2} sx={{ p: 2 }}>

                <Grid size={12}>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: "6px",
                    }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: "18px", sm: "32px", md: "36px" },
                                    lineHeight: { xs: "16px", md: "48px" },
                                    color: "#252C32",
                                    fontFamily: "Outfit, sans-serif",
                                }}
                            >
                                Risk Exposure
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: "24px",
                                    color: "#252C32",
                                    fontFamily: "Outfit, sans-serif",
                                }}
                            >
                                Prioritize active threats, mitigation effectiveness, and emerging delivery exposure.
                            </Typography>
                        </Box>
                        <Stack alignItems="flex-end">
                            <Stack direction="row" spacing={2} alignItems="center">
                                {filters.wbsName && (
                                    <Chip
                                        label={
                                            <Typography variant="body2">
                                                <strong style={{ color: "#0E73F6" }}>Filter:</strong>{" "}
                                                <span style={{ fontWeight: 400, color: "#6B7280" }}>
                                                    {filters.wbsName}
                                                </span>
                                            </Typography>
                                        }
                                        sx={{
                                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                                            border: 0
                                        }}
                                        variant="outlined"
                                        onDelete={resetAllFilters}
                                    />
                                )}

                                <Button
                                    variant="outlined"
                                    startIcon={<FilterAltOutlinedIcon sx={{ fontSize: "18px !important" }} />}
                                    aria-label="Open risk filters"
                                    ref={anchorRef}
                                    onClick={handleToggle}
                                    sx={{
                                        minHeight: 34,
                                        px: 1.5,
                                        borderColor: enterpriseTokens.line,
                                        color: enterpriseTokens.slate,
                                        backgroundColor: open ? enterpriseTokens.surfaceTint : "#FFFFFF",
                                        "&:hover": { borderColor: "#94A3B8", backgroundColor: enterpriseTokens.surfaceAlt },
                                    }}
                                >
                                    Filters
                                </Button>
                                <Popper
                                    open={open}
                                    anchorEl={anchorRef.current}
                                    placement="bottom-end"
                                    transition
                                    disablePortal={false}
                                    // sx={{ zIndex: 4000 }}
                                    onClick={(e) => e.stopPropagation()}
                                    modifiers={[
                                        {
                                            name: 'arrow',
                                            enabled: true,
                                            options: {
                                                element: arrowRef?.current,
                                            },
                                        },
                                    ]}
                                >
                                    {({ TransitionProps }) => (
                                        <Grow {...TransitionProps}>
                                            <Paper
                                                sx={{
                                                    p: 2,
                                                    // borderRadius: 1,
                                                    boxShadow: "none",
                                                    width: 300,
                                                    borderRadius: 3,
                                                    border: "2px solid #eef0f4",
                                                    position: 'relative'
                                                }}
                                            >
                                                <ArrowDropUpRoundedIcon
                                                    ref={arrowRef}
                                                    sx={{
                                                        position: "absolute",
                                                        top: "-33px",
                                                        right: "0px",
                                                        fontSize: "60px",
                                                        color: "white",
                                                        filter: "drop-shadow(0px -1px 1px rgba(0,0,0,0.15))",
                                                    }}
                                                />
                                                <ClickAwayListener onClickAway={handleClose}>
                                                    <Box>
                                                        {open && (

                                                            <Formik
                                                                initialValues={filters}
                                                                validationSchema={validationSchema}
                                                                onSubmit={handleApplyFilters}
                                                                enableReinitialize
                                                            >
                                                                {({ values,
                                                                    errors,
                                                                    touched,
                                                                    handleChange,
                                                                    handleBlur,
                                                                    handleSubmit,
                                                                    isSubmitting,
                                                                    setFieldValue,
                                                                    resetForm,
                                                                    setFieldTouched,
                                                                    setTouched }) => {
                                                                    // const subCatOptions = values?.category
                                                                    //     ? subCategoryMap[values?.category]
                                                                    //     : [];

                                                                    return (
                                                                        <Form>
                                                                            <Grid container spacing={2}>
                                                                                {/* ... your Category + Sub Category stuff ... */}

                                                                                {/* WBS TREE SECTION */}
                                                                                <Grid size={{ xs: 12, md: 12 }}>
                                                                                    <Typography sx={{ mb: 1, mt: 1 }}>
                                                                                        WBS Selector
                                                                                    </Typography>

                                                                                    {/* <WbsTreeView
                                                                                    projectId={projectId}
                                                                                    onSelect={function (node) {
                                                                                        // node = { id, name, raw, isLeaf }

                                                                                        // Save into form values
                                                                                        setFieldValue("wbsId", node.id);
                                                                                        setFieldValue("wbsName", node.name);
                                                                                    }}
                                                                                /> */}

                                                                                    {/* Optional: show selected WBS name inside popup */}
                                                                                    {values.wbsName && (
                                                                                        <Typography sx={{ mt: 1, fontSize: 12, color: "#555" }}>
                                                                                            Selected WBS: <strong>{values.wbsName}</strong>
                                                                                        </Typography>
                                                                                    )}
                                                                                </Grid>

                                                                                {/* Apply */}
                                                                                <Grid size={12}>
                                                                                    <Box
                                                                                        sx={{
                                                                                            display: "flex",
                                                                                            justifyContent: "flex-end",
                                                                                            alignItems: "center"
                                                                                        }}
                                                                                    >
                                                                                        {isApplyFilter && (
                                                                                            <Button
                                                                                                onClick={function () {
                                                                                                    handleReset(resetForm);
                                                                                                }}
                                                                                                sx={{
                                                                                                    textTransform: "none",
                                                                                                    color: "#F15744",
                                                                                                    fontSize: "14px",
                                                                                                    lineHeight: "100%",
                                                                                                    fontFamily: "Outfit, sans-serif",
                                                                                                    borderRadius: "25px",
                                                                                                    fontWeight: 500,
                                                                                                    px: "20px",
                                                                                                    py: "15px",
                                                                                                }}
                                                                                            >
                                                                                                Reset
                                                                                            </Button>
                                                                                        )}

                                                                                        <Button
                                                                                            type="submit"
                                                                                            variant="contained"
                                                                                            disabled={isSubmitting}
                                                                                            sx={{
                                                                                                backgroundColor: "#00344C",
                                                                                                borderRadius: "25px",
                                                                                                textTransform: "none",
                                                                                                fontWeight: 500,
                                                                                                fontSize: "14px",
                                                                                                lineHeight: "100%",
                                                                                                fontFamily: "Outfit, sans-serif",
                                                                                                color: "#FFFFFF",
                                                                                                px: "20px",
                                                                                                py: "15px",
                                                                                            }}
                                                                                        >
                                                                                            Apply
                                                                                        </Button>
                                                                                    </Box>
                                                                                </Grid>
                                                                            </Grid>

                                                                            {/* <Grid container spacing={2}>
                                                                            <Grid size={{ xs: 12, md: 12 }}>
                                                                                <Typography sx={{ mb: 1, mt: 1 }}>
                                                                                    Category
                                                                                </Typography>
                                                                                <Autocomplete
                                                                                    options={categoryOptions}
                                                                                    loading={isLoadingCategories}
                                                                                    getOptionLabel={(option) => option?.label || ''}
                                                                                    value={
                                                                                        categoryOptions?.find(
                                                                                            (opt) => opt.value == values?.category
                                                                                        ) || null
                                                                                    }
                                                                                    onChange={(e, newValue) => {
                                                                                        const id = newValue?.value ?? null;
                                                                                        setFieldValue("category", id);
                                                                                        setFieldValue("subCategory", "");
                                                                                        setSelectedCategoryId(id || null);
                                                                                    }}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            placeholder="Select Category"
                                                                                            name="category"
                                                                                            size="small"
                                                                                            sx={{
                                                                                                "& input::placeholder": {
                                                                                                    fontWeight: 400,
                                                                                                    fontSize: "16px",
                                                                                                    lineHeight: "100%",
                                                                                                    letterSpacing: "0.16px",
                                                                                                    color: "#5F5F5F",
                                                                                                    fontFamily: "Outfit, sans-serif",
                                                                                                    opacity: 1,
                                                                                                },
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                    ListboxProps={{ sx: { fontSize: '13px' } }}
                                                                                />
                                                                                {touched?.category && errors?.category && (
                                                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                        {errors?.category}
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>
                                                                            <Grid size={{ xs: 12, md: 12 }}>
                                                                                <Typography sx={{ mb: 1, mt: 1 }}>
                                                                                    Sub Category
                                                                                </Typography>
                                                                                <Autocomplete
                                                                                    options={subCatOptions}
                                                                                    loading={isLoadingSubCategories}
                                                                                    getOptionLabel={(option) => option?.label || ''}
                                                                                    value={
                                                                                        subCatOptions?.find(
                                                                                            (opt) => opt.value == values?.subCategory
                                                                                        ) || null
                                                                                    }
                                                                                    onChange={(e, newValue) =>{
                                                                                        const id = newValue?.value ?? "";

                                                                                        setFieldValue("subCategory", id);

                                                                                        // 🔥 This triggers Level 3 API call
                                                                                        setSelectedSubCategoryId(id || null);
                                                                                    }}
                                                                                    disabled={!values.category}
                                                                                    renderInput={(params) => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            placeholder="Sub Category"
                                                                                            name={`status`}
                                                                                            size="small"
                                                                                            sx={{
                                                                                                "& input::placeholder": {
                                                                                                    fontWeight: 400,
                                                                                                    fontSize: "16px",
                                                                                                    lineHeight: "100%",
                                                                                                    letterSpacing: "0.16px",
                                                                                                    color: "#5F5F5F",
                                                                                                    fontFamily: "Outfit, sans-serif",
                                                                                                    opacity: 1,
                                                                                                },
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                    ListboxProps={{ sx: { fontSize: '13px' } }}
                                                                                />
                                                                                {touched?.subCategory && errors?.subCategory && (
                                                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                        {errors?.subCategory}
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>
                                                                            <Grid size={{ xs: 12, md: 12 }}>
                                                                                <Typography sx={{ mb: 1, mt: 1 }}>
                                                                                    Activity
                                                                                </Typography>

                                                                                <Autocomplete
                                                                                    options={level3Options}
                                                                                    loading={isLoadingLevel3}
                                                                                    getOptionLabel={(option) => option?.label || ""}
                                                                                    value={
                                                                                    level3Options.find(
                                                                                        (opt) => opt.value === values.activity
                                                                                    ) || null
                                                                                    }
                                                                                    onChange={(e, newValue) =>
                                                                                    setFieldValue("activity", newValue?.value ?? "")
                                                                                    }
                                                                                    disabled={!values.subCategory}
                                                                                    renderInput={(params) => (
                                                                                    <TextField
                                                                                        {...params}
                                                                                        placeholder="Select Activity"
                                                                                        name="activity"
                                                                                        size="small"
                                                                                        sx={{
                                                                                        "& input::placeholder": {
                                                                                            fontWeight: 400,
                                                                                            fontSize: "16px",
                                                                                            lineHeight: "100%",
                                                                                            letterSpacing: "0.16px",
                                                                                            color: "#5F5F5F",
                                                                                            fontFamily: "Outfit, sans-serif",
                                                                                            opacity: 1,
                                                                                        },
                                                                                        }}
                                                                                    />
                                                                                    )}
                                                                                    ListboxProps={{ sx: { fontSize: "13px" } }}
                                                                                />
                                                                                </Grid>
                                                                            <Grid size={12}>
                                                                                <Box
                                                                                    sx={{
                                                                                        display: "flex",
                                                                                        justifyContent: "flex-end",
                                                                                        alignItems: "center",
                                                                                    }}
                                                                                >
                                                                                    {isApplyFilter &&
                                                                                        <Button onClick={handleReset}
                                                                                            sx={{
                                                                                                textTransform: "none",
                                                                                                color: "#F15744",
                                                                                                fontSize: "14px",
                                                                                                lineHeight: "100%",
                                                                                                fontFamily: "Outfit, sans-serif",
                                                                                                borderRadius: "25px",
                                                                                                fontWeight: 500,
                                                                                                px: "20px",
                                                                                                py: "15px",
                                                                                            }}>
                                                                                            Reset
                                                                                        </Button>
                                                                                    }
                                                                                    <Button
                                                                                        type="submit"
                                                                                        variant="contained"
                                                                                        // loading={addActionTrackerRes?.isLoading}
                                                                                        sx={{
                                                                                            backgroundColor: "#00344C",
                                                                                            borderRadius: "25px",
                                                                                            textTransform: "none",
                                                                                            fontWeight: 500,
                                                                                            fontSize: "14px",
                                                                                            lineHeight: "100%",
                                                                                            fontFamily: "Outfit, sans-serif",
                                                                                            color: "#FFFFFF",
                                                                                            px: "20px",
                                                                                            py: "15px",
                                                                                        }}
                                                                                    >
                                                                                        Apply
                                                                                    </Button>
                                                                                </Box>
                                                                            </Grid>
                                                                        </Grid> */}
                                                                        </Form>
                                                                    );
                                                                }}
                                                            </Formik>

                                                        )}
                                                    </Box>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>
                            </Stack>
                        </Stack>
                    </Box>
                </Grid>
                <Grid size={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", pb: 0.5, borderBottom: `1px solid ${enterpriseTokens.lineSoft}`, "& .MuiTypography-root": { fontSize: "12px", color: enterpriseTokens.muted } }}>
                        <ProjectLastUpdatedOn date={riskData?.risk} />
                    </Box>
                </Grid>
                <Grid container spacing={1.5}>
                    <Grid size={{ md: 12 }}>

                        <Box sx={{
                            background: "transparent",
                            borderRadius: 0,
                            mt: 1
                            // p: 2,
                            // boxShadow: "0px 4px 20px rgba(0,0,0,0.05)"
                        }}>
                            <RiskKpi kpiData={riskKpiData} />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                        <HeatMap data={riskHeatMapData} />
                        {/* <RiskCountBySeverity data={riskSeveritySummaryData} /> */}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                        <RiskByCategory data={riskCategorySummaryData} />
                    </Grid>
                    {/* <Grid size={{ xs: 12, md: 6 }}>
                        <HeatMap data={riskHeatMapData} />
                    </Grid> */}
                    {/* <Grid size={{ xs: 12, md: 6 }}>
                        <ActiveRiskByOwners data={riskOwnerSummaryData} />
                    </Grid> */}
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                        <RiskTrend data={riskTrendData} />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} sx={{ display: "flex" }}>
                        <RiskAgeingAnalysis data={riskAgeingData} />
                        {/* <RiskTrend data={riskTrendData} /> */}
                    </Grid>
                </Grid>

            </Grid >
        </>
    );
};

export default RiskDashboard;
